import { useCallback, useMemo, useState } from 'react';
import _get from 'lodash/get';
import {
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  Task,
  TaskCompletionScopeNameEnum,
  TaskScopeInputObject,
} from '@lib/graphql-common';
import FieldNames from '@lib/enums/fieldNames/interventionFieldNames';
import { logger } from '@lib/services/logger';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { FieldNames as TaskFilterFieldNames } from '../TasksTableMultiselect/utils/getFilterFields';
import {
  TaskAndCompletionsModalProps,
  TaskAndCompletionsModalStep,
} from './TaskAndCompletionsModal';

export type FieldOption = { value?: string; label?: string } | null;

// An Intervention only covers completions the backend's
// `eligible_for_intervention` policy accepts — non-conform, unresolved and
// not covered yet. The create mutation validates against that same policy
// (rejecting the whole selection on any ineligible id), so both steps read
// it through their scopes rather than approximating it with filters.
// "Success" can never be eligible, so don't offer it in the Completions
// step's own Status filter.
const COMPLETIONS_EXCLUDED_STATUS_OPTIONS = [
  FormTemplateQuestionFieldOptionsValidityHintEnum.Success,
];

const SET_VALUE_OPTIONS = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

export type UseTaskAndCompletionsPickerResult = {
  // The form's current Site / Site Area, kept fresh (see the hook's doc) —
  // for other location-scoped pickers on the same form.
  site: FieldOption;
  siteArea: FieldOption;
  openTaskStep: () => void;
  openCompletionsStep: () => void;
  // Wire to the form's Site / Site Area / Asset `fieldChangeCallBack`s.
  onSiteChange: (site: FieldOption) => void;
  onSiteAreaChange: (siteArea: FieldOption) => void;
  onAssetChange: (asset: FieldOption) => void;
  modalProps: Omit<
    TaskAndCompletionsModalProps,
    'taskFilterLoadOptions' | 'completionUrls'
  >;
};

/**
 * State and wiring for the Intervention form's Task -> Completions wizard.
 *
 * `formRef.current.values` only reflects FormContainer's own last render,
 * which doesn't follow every change once the form is dirty — so Site, Site
 * Area, Asset and the picked Task's groupKey are mirrored into state from the
 * form's change callbacks, and the ref read only covers the initial values.
 * `undefined` means "not changed yet, read the ref"; `null` means cleared.
 */
export default function useTaskAndCompletionsPicker(
  formRef: FormRefType,
): UseTaskAndCompletionsPickerResult {
  const [step, setStep] = useState<TaskAndCompletionsModalStep | null>(null);
  const [siteOverride, setSiteOverride] = useState<FieldOption | undefined>();
  const [siteAreaOverride, setSiteAreaOverride] = useState<
    FieldOption | undefined
  >();
  const [assetOverride, setAssetOverride] = useState<FieldOption | undefined>();
  const [taskGroupKeyOverride, setTaskGroupKeyOverride] = useState<
    string | null
  >(null);

  const formValues = formRef.current?.values;
  const site =
    siteOverride !== undefined
      ? siteOverride
      : (_get(formValues, FieldNames.Site) as FieldOption);
  const siteArea =
    siteAreaOverride !== undefined
      ? siteAreaOverride
      : (_get(formValues, FieldNames.SiteArea) as FieldOption);
  const asset =
    assetOverride !== undefined
      ? assetOverride
      : (_get(formValues, FieldNames.Asset) as FieldOption);

  const onSiteChange = useCallback((value: FieldOption) => {
    // Site's own change callback resets Site Area, Asset and Task on the form.
    setSiteOverride(value);
    setSiteAreaOverride(null);
    setAssetOverride(null);
    setTaskGroupKeyOverride(null);
  }, []);

  const onSiteAreaChange = useCallback((value: FieldOption) => {
    // Site Area's own change callback resets Asset and Task on the form.
    setSiteAreaOverride(value);
    setAssetOverride(null);
    setTaskGroupKeyOverride(null);
  }, []);

  const onAssetChange = useCallback((value: FieldOption) => {
    // Asset's own change callback resets Task on the form.
    setAssetOverride(value);
    setTaskGroupKeyOverride(null);
  }, []);

  const onTaskSelect = useCallback(
    (values: string[], selectedItems?: Task[]) => {
      if (!formRef.current) return;
      const task = selectedItems?.[0];
      const groupKey = values[0] || null;
      logger.info('Intervention form: task picked', { groupKey });
      setTaskGroupKeyOverride(groupKey);
      formRef.current.setValue(
        FieldNames.Task,
        groupKey && task ? { value: groupKey, label: task.name } : null,
        SET_VALUE_OPTIONS,
      );
    },
    [formRef],
  );

  const onCompletionsSelect = useCallback(
    (values: string[]) => {
      logger.info('Intervention form: completions picked', {
        count: values.length,
      });
      formRef.current?.setValue(
        FieldNames.Completions,
        values.map((completionId) => ({ value: completionId })),
        SET_VALUE_OPTIONS,
      );
    },
    [formRef],
  );

  // The Task step only offers tasks with a check the intervention may still
  // cover at a location that doesn't conflict with the form's: the scope
  // takes the form's Site / Site Area / Asset, and leaves in tasks with no
  // area or equipment of their own. Whichever of the form's Site / Site Area
  // / Asset are set are also pinned as locked filters — those narrow to
  // exact matches, so the picker only offers tasks at the entities the
  // intervention targets. Site Area and Equipment overrides are `[]` when
  // unset because the picker shallow-merges overrides on open — an omitted
  // key couldn't clear a value the user picked last time.
  const siteId = site?.value;
  const siteLabel = site?.label;
  const siteAreaId = siteArea?.value;
  const siteAreaLabel = siteArea?.label;
  const assetId = asset?.value;
  const assetLabel = asset?.label;
  const taskScope = useMemo<TaskScopeInputObject>(
    () => ({
      custom: {
        eligibleForIntervention: {
          siteId: siteId || undefined,
          siteAreaId: siteAreaId || undefined,
          assetId: assetId || undefined,
        },
      },
    }),
    [siteId, siteAreaId, assetId],
  );
  const taskFilterOverrides = useMemo(() => {
    if (!siteId) return undefined;
    return {
      [TaskFilterFieldNames.Sites]: [{ value: siteId, label: siteLabel }],
      [TaskFilterFieldNames.SiteArea]: siteAreaId
        ? [{ value: siteAreaId, label: siteAreaLabel }]
        : [],
      [TaskFilterFieldNames.Equipment]: assetId
        ? [{ value: assetId, label: assetLabel }]
        : [],
    };
  }, [siteId, siteLabel, siteAreaId, siteAreaLabel, assetId, assetLabel]);
  const taskDisabledFilterFields = useMemo(
    () => [
      TaskFilterFieldNames.Sectors,
      TaskFilterFieldNames.Sites,
      ...(siteAreaId ? [TaskFilterFieldNames.SiteArea] : []),
      ...(assetId ? [TaskFilterFieldNames.Equipment] : []),
    ],
    [siteAreaId, assetId],
  );

  const onClose = useCallback(() => setStep(null), []);

  return {
    site,
    siteArea,
    openTaskStep: useCallback(() => setStep('task'), []),
    openCompletionsStep: useCallback(() => setStep('completions'), []),
    onSiteChange,
    onSiteAreaChange,
    onAssetChange,
    modalProps: {
      isOpen: step !== null,
      step: step ?? 'task',
      onStepChange: setStep,
      onClose,
      formRef,
      taskFieldName: FieldNames.Task,
      completionsFieldName: FieldNames.Completions,
      onTaskSelect,
      onCompletionsSelect,
      completionsGroupKey:
        taskGroupKeyOverride ??
        (_get(formValues, [FieldNames.Task, 'value']) as string | undefined),
      taskFilterOverrides,
      taskDisabledFilterFields,
      taskScope,
      completionsScope: TaskCompletionScopeNameEnum.EligibleForIntervention,
      completionsExcludedStatusFilterOptions:
        COMPLETIONS_EXCLUDED_STATUS_OPTIONS,
    },
  };
}
